import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import Select from 'react-select';
import { Grid } from '@material-ui/core';
import { NumberListInput } from '../../store/inputs/NumberListInput';

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    borderColor: state.isFocused ? '#1a73e8' : '#e0e0e0',
    borderWidth: '1px',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(26,115,232,0.12)' : 'none',
    borderRadius: '8px',
    minHeight: '38px',
    fontSize: '13px',
    '&:hover': { borderColor: '#bdbdbd' },
  }),
  placeholder: (base: any) => ({ ...base, color: '#9e9e9e', fontSize: '13px' }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: '13px',
    padding: '8px 12px',
    backgroundColor: state.isSelected ? '#e8f0fe' : state.isFocused ? '#f5f5f5' : '#fff',
    color: '#424242',
    '&:active': { backgroundColor: '#e8f0fe' },
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
    overflow: 'hidden',
    zIndex: 10,
  }),
  singleValue: (base: any) => ({ ...base, fontSize: '13px', color: '#424242' }),
};

const CDropDownListInput = observer(({ item }: { item: NumberListInput }) => {
  const {
    label,
    value,
    dropDownValues,
    onChangeValue,
    disabled,
    tag,
    isKey,
    isEdit,
  } = item;
  const [items, setItems] = useState<any[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const prevValueRef = useRef<string>('');

  // Sync from MobX observable — use JSON to detect deep changes
  useEffect(() => {
    const raw = Array.isArray(value) ? value.slice() : [];
    const json = JSON.stringify(raw);
    if (json !== prevValueRef.current) {
      prevValueRef.current = json;
      setItems(raw);
    }
  });

  const getDisplayLabel = (val: any): string => {
    if (dropDownValues) {
      const entry = Object.entries(dropDownValues).find(([k]) => k === val);
      if (entry) return String(entry[1]);
    }
    return String(val);
  };

  const addItem = () => {
    if (!selectedKey) return;
    if (items.includes(selectedKey)) return;
    const next = [...items, selectedKey];
    onChangeValue(next as any);
    setItems(next);
    prevValueRef.current = JSON.stringify(next);
    setSelectedKey(null);
  };

  const removeItem = (val: any) => {
    const next = items.filter((n: any) => n !== val);
    onChangeValue(next as any);
    setItems(next);
    prevValueRef.current = JSON.stringify(next);
    setSelectedKey(null);
  };

  const isLocked = disabled || (isKey && isEdit);

  const availableOptions = Object.entries(dropDownValues || {})
    .filter(([key]) => !items.includes(key))
    .map(([key, displayLabel]) => ({
      label: String(displayLabel),
      value: key,
    }));

  return (
    <Grid container spacing={3}>
      <Grid item xs={11} style={{ margin: '0 auto' }}>
        <div
          style={{
            border: '1px solid #e0e0e0',
            borderRadius: '10px',
            padding: '14px 16px',
            margin: '8px 0',
            background: '#fff',
          }}
        >
          {/* Label */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <i
              className={isKey ? 'bx bx-key' : 'bx bx-list-check'}
              style={{ fontSize: '15px', color: '#757575', marginRight: '6px' }}
            />
            <span style={{ fontSize: '12px', fontWeight: 500, color: '#757575', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              {label}
            </span>
          </div>

          {/* Select + Add */}
          {!isLocked && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: items.length > 0 ? '10px' : '0' }}>
              <div style={{ flex: 1 }}>
                <Select
                  id={tag}
                  placeholder="Select..."
                  isDisabled={isLocked || availableOptions.length === 0}
                  onChange={(v) => {
                    if (v) setSelectedKey((v as { label: string; value: string }).value);
                  }}
                  options={availableOptions}
                  value={
                    selectedKey
                      ? availableOptions.find((o) => o.value === selectedKey) || null
                      : null
                  }
                  styles={selectStyles}
                  noOptionsMessage={() => 'All selected'}
                />
              </div>
              <button
                onClick={() => addItem()}
                disabled={!selectedKey}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '38px',
                  height: '38px',
                  border: !selectedKey ? '1px solid #e0e0e0' : '1px solid #1a73e8',
                  borderRadius: '8px',
                  background: !selectedKey ? '#fafafa' : '#1a73e8',
                  color: !selectedKey ? '#bdbdbd' : '#fff',
                  cursor: !selectedKey ? 'default' : 'pointer',
                  flexShrink: 0,
                  transition: 'all 0.15s ease',
                  outline: 'none',
                }}
                title="Add"
              >
                <i className="bx bx-plus" style={{ fontSize: '18px' }} />
              </button>
            </div>
          )}

          {/* Selected chips */}
          {items.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {items.map((val, index) => (
                <div
                  key={`${val}${index}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '4px 8px 4px 10px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '20px',
                    background: '#fafafa',
                    fontSize: '12px',
                    color: '#424242',
                    lineHeight: '1.5',
                  }}
                >
                  <i className="bx bxs-circle" style={{ fontSize: '6px', color: '#66bb6a' }} />
                  <span>{getDisplayLabel(val)}</span>
                  {!isLocked && (
                    <i
                      className="bx bx-x"
                      onClick={() => removeItem(val)}
                      style={{
                        fontSize: '15px',
                        color: '#bdbdbd',
                        cursor: 'pointer',
                        marginLeft: '2px',
                        transition: 'color 0.15s ease',
                      }}
                      onMouseEnter={(e) => { (e.target as HTMLElement).style.color = '#e53935'; }}
                      onMouseLeave={(e) => { (e.target as HTMLElement).style.color = '#bdbdbd'; }}
                    />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <span style={{ fontSize: '12px', color: '#bdbdbd', fontStyle: 'italic' }}>No items selected</span>
          )}
        </div>
      </Grid>
    </Grid>
  );
});

export default CDropDownListInput;
